import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RefreshCw } from "lucide-react";
import { errText } from "./docFile";

const NEG = /\b(used|old|older|second[\s-]?hand|refurb(ished)?|pre[\s-]?owned|pre[\s-]?loved|reconditioned|renewed|recycled|re[\s-]?deployed|ex[\s-]?demo)\b/i;

export function LabelContentDialog({ dc, defaultMsg, onClose, onSaved, onPreview }) {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (dc) setMsg(dc.welcome_message || defaultMsg); }, [dc, defaultMsg]);
  if (!dc) return null;
  const bad = msg.match(NEG);
  const call = async (body, ok) => {
    setBusy(true);
    try { await api.put(`/document-centre/dcs/${dc.id}/label-content`, body); toast.success(ok); onSaved(); } catch (e) { toast.error(await errText(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="label-content-dialog">
        <DialogHeader><DialogTitle className="text-[14px] font-bold">Label content · {dc.recipient_name}</DialogTitle></DialogHeader>
        <div className="space-y-1">
          <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-600">Welcome message</div>
          <p className="text-[11px] text-slate-500">Printed under “{dc.subsidiary || "employer name"}”. Positive wording only — never describe the device as used, old, second-hand or refurbished.</p>
          <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} maxLength={220} className={`w-full rounded-md border px-2.5 py-2 text-[12.5px] ${bad ? "border-rose-400" : "border-slate-300"}`} data-testid="label-welcome-message" />
          <div className="flex items-center gap-2 text-[10.5px]">
            {bad ? <span className="font-semibold text-rose-600" data-testid="label-welcome-warning">“{bad[0]}” is not allowed on employee-facing material</span> : <span className="text-slate-400">{msg.length}/220</span>}
            <button onClick={() => call({ reset_message: true }, "Default message restored")} className="ml-auto text-[11px] font-semibold text-slate-500 hover:underline" data-testid="label-welcome-reset">Use default</button>
            <button disabled={busy || !!bad || msg.trim().length < 10} onClick={() => call({ welcome_message: msg }, "Welcome message saved")} className="rounded-full bg-slate-900 px-3 py-1 text-[11px] font-semibold text-white disabled:opacity-40" data-testid="label-welcome-save">Save message</button>
          </div>
        </div>
        <div className="mt-2 flex items-center gap-2 rounded-lg bg-slate-50 p-3">
          <div className="flex-1 text-[11.5px] text-slate-600"><b className="text-slate-800">TIP label</b> rotates approved device-care and productivity tips per device.</div>
          <button disabled={busy} onClick={() => call({ tip_shift: 1 }, "Next approved tip selected")} className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1 text-[11px] font-semibold hover:bg-slate-100" data-testid="label-next-tip"><RefreshCw className="h-3.5 w-3.5" />Next tip</button>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={() => onPreview("tip_label")} className="rounded-full border border-slate-300 px-3 py-1.5 text-[11.5px] font-semibold" data-testid="label-preview-tip">Preview TIP</button>
          <button onClick={() => onPreview("welcome_label")} className="rounded-full border border-slate-300 px-3 py-1.5 text-[11.5px] font-semibold" data-testid="label-preview-welcome">Preview WELCOME</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
